import { useEffect, useMemo, useState } from 'react';
import Loader from '../../../common/components/Loader';
import { type CategoryConfig } from '../CopProductCatalog';
import { NO_PRODUCT_CATEGORIES_MESSAGE } from '../constants/categories';
import {
  type BusinessUnit,
  type ProductCategory,
  fetchBusinessUnits,
  fetchProductCategories,
} from '../services/product.catalog.tabs.service';

type ProductCatalogTabsProps = {
  categoriesConfig?: CategoryConfig[];
};

const ProductCatalogTabs = ({ categoriesConfig = [] }: ProductCatalogTabsProps) => {
  const [tabs, setTabs] = useState<BusinessUnit[]>([]);
  const [activeTabId, setActiveTabId] = useState<number | null>(null);
  const [subCategories, setSubCategories] = useState<ProductCategory[]>([]);
  const [tabsLoading, setTabsLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setTabsLoading(true);
    fetchBusinessUnits()
      .then((units) => {
        if (cancelled) return;
        setTabs(units);
        if (units.length > 0) {
          setActiveTabId(units[0].id);
        }
      })
      .finally(() => {
        if (!cancelled) setTabsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeTabId === null) return;
    let cancelled = false;
    setSubLoading(true);
    fetchProductCategories(activeTabId)
      .then((categories) => {
        if (cancelled) return;
        setSubCategories(categories);
      })
      .catch(() => {
        if (!cancelled) setSubCategories([]);
      })
      .finally(() => {
        if (!cancelled) setSubLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTabId]);

  const handleTabClick = (id: number) => {
    setActiveTabId(id);
  };

  const familyLinks = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    categoriesConfig.forEach((cat) => {
      (cat.families ?? []).forEach(({ familyTitle, familyLink }) => {
        if (familyTitle && familyLink) map[familyTitle] = familyLink;
      });
    });
    return map;
  }, [categoriesConfig]);

  if (tabsLoading) {
    return (
      <div className="cop-product-catalog__tabs-loading">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="cop-product-catalog__tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTabId === tab.id}
            className={`cop-product-catalog__tab${activeTabId === tab.id ? ' cop-product-catalog__tab--active' : ''
              }`}
            onClick={() => handleTabClick(tab.id)}
            type="button"
          >
            {tab.name}
          </button>
        ))}
      </div>

      {subLoading ? (
        <div className="cop-product-catalog__panel-loading">
          <Loader />
        </div>
      ) : null}

      {!subLoading && subCategories.length > 0 && (
        <div
          className="cop-product-catalog__panel"
          role="tabpanel"
          aria-label={tabs.find((t) => t.id === activeTabId)?.name ?? ''}
        >
          <div className="cop-product-catalog__cards">
            {subCategories.map((sub) => {
              const baseLink = familyLinks[sub.name];
              return baseLink ? (
                <a
                  key={sub.id}
                  href={`${baseLink}?product-category-name=${encodeURIComponent(sub.name)}&product-category-id=${sub.id}`}
                  className="cop-product-catalog__card"
                  aria-label={sub.name}
                >
                  <span className="cop-product-catalog__card-name">{sub.name}</span>
                  <span className="cop-product-catalog__card-arrow" aria-hidden="true">&#8250;</span>
                </a>
              ) : (
                <button
                  key={sub.id}
                  type="button"
                  className="cop-product-catalog__card"
                  aria-label={sub.name}
                >
                  <span className="cop-product-catalog__card-name">{sub.name}</span>
                  <span className="cop-product-catalog__card-arrow" aria-hidden="true">&#8250;</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!subLoading && subCategories.length === 0 && (
        <p className="cop-product-catalog__empty-message">{NO_PRODUCT_CATEGORIES_MESSAGE}</p>
      )}
    </>
  );
};

export default ProductCatalogTabs;